// VideoSource: wraps a decoded video file as a muted <video> element used for
// preview compositing. One element per asset — it is the playback head the
// compositor seeks/plays. Audio is handled separately (decoded into the mixer),
// so this element is always muted. Kept in a side map on the Store, out of undo
// snapshots, exactly like decoded AudioBuffers.

export interface VideoMeta { width: number; height: number; duration: number; }

export class VideoSource {
  constructor(
    public el: HTMLVideoElement,
    public url: string,
    public width: number,
    public height: number,
    public duration: number,
    /** Keep the original file blob so the project can be saved/re-exported. */
    public blob: Blob,
  ) {}

  /** Load a video blob into a muted, frame-accurate <video> element. Always settles
   *  — a hard timeout guards against files the browser neither loads nor errors on. */
  static load(blob: Blob): Promise<VideoSource> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const el = document.createElement("video");
      el.muted = true;
      el.playsInline = true;
      el.preload = "auto";
      el.crossOrigin = "anonymous";
      el.src = url;

      let settled = false;
      let durTimer: any = null;
      let loadTimer: any = null;
      const cleanup = () => {
        clearTimeout(loadTimer); clearTimeout(durTimer);
        el.removeEventListener("loadedmetadata", onMeta);
        el.removeEventListener("error", onErr);
      };
      const finish = () => {
        if (settled) return; settled = true; cleanup();
        const dur = isFinite(el.duration) && el.duration > 0 ? el.duration : 0;
        resolve(new VideoSource(el, url, el.videoWidth || 1920, el.videoHeight || 1080, dur, blob));
      };
      const fail = (msg: string) => {
        if (settled) return; settled = true; cleanup();
        try { URL.revokeObjectURL(url); } catch {}
        reject(new Error(msg));
      };
      const onMeta = () => {
        // Some streams (MediaRecorder webm, fragmented files) report Infinity/NaN
        // duration until seeked to the end — force the browser to compute it, but
        // give up after a moment so a file that never reports duration can't hang.
        if (!isFinite(el.duration) || el.duration === 0) {
          const onDur = () => {
            if (isFinite(el.duration) && el.duration > 0) {
              el.removeEventListener("durationchange", onDur);
              el.currentTime = 0;
              finish();
            }
          };
          el.addEventListener("durationchange", onDur);
          durTimer = setTimeout(() => { el.removeEventListener("durationchange", onDur); finish(); }, 4000);
          try { el.currentTime = 1e101; } catch { finish(); }
        } else finish();
      };
      const onErr = () => fail("The browser can't decode this video (unsupported codec or container).");
      // Neither metadata nor error within this window → treat as unsupported.
      loadTimer = setTimeout(() => fail("Timed out loading the video — the browser may not support this format."), 30000);
      el.addEventListener("loadedmetadata", onMeta);
      el.addEventListener("error", onErr);
    });
  }

  /** Seek the element and resolve once the target frame is ready to draw. */
  seekTo(time: number): Promise<void> {
    const t = Math.max(0, Math.min(this.duration || 0, time));
    return new Promise((resolve) => {
      // Already there (within ~half a frame) — nothing to wait for.
      if (Math.abs(this.el.currentTime - t) < 0.001 && this.el.readyState >= 2) { resolve(); return; }
      const onSeeked = () => { this.el.removeEventListener("seeked", onSeeked); resolve(); };
      this.el.addEventListener("seeked", onSeeked);
      this.el.currentTime = t;
    });
  }

  dispose() {
    try { this.el.pause(); } catch {}
    this.el.removeAttribute("src");
    try { this.el.load(); } catch {}
    URL.revokeObjectURL(this.url);
  }
}

/** Create a throwaway video element from the same blob URL — used by the
 *  timeline to extract thumbnail frames without fighting the compositor's
 *  playback element for currentTime. */
export function makeThumbnailVideo(url: string): HTMLVideoElement {
  const el = document.createElement("video");
  el.muted = true;
  el.playsInline = true;
  el.preload = "auto";
  el.crossOrigin = "anonymous";
  el.src = url;
  return el;
}
